var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, CircularProgress, Grid, Skeleton, TextField, Tooltip, Typography } from "@mui/material";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import PageHeader from "@ui/components/header/PageHeader";
import { PageContainer, PageContent } from "@ui/components/wraper";
import { FInput } from "@ui/components/inputs/FInput";
import { SButton } from "@ui/components/buttons/SButton";
import { SIconButton } from "@ui/components/buttons";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { toast } from "@ui/utils/toaster";
import { usePermissions } from "@ui/hooks/usePermissions";
import { Permission } from "@utility/permission-checker";
import { accessPointsApi } from "@api/enpoints/access-points-api";
import { apiErrorMessage, clientsApi } from "@api/enpoints/clients-api";
const emptyLocation = () => ({ locationId: "", name: "", inUse: false });
/**
 * Create / edit a client. The client name can be picked from the access point names (a convenience only: it fills
 * the text, nothing is linked) or typed freely. Locations keep their id when edited; one used by an access point
 * cannot be removed (also refused by the API).
 */
const AddEditClientPage = () => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { clientId } = useParams();
    const isEdit = !!clientId;
    const [loadingClient, setLoadingClient] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState(null);
    // AP name suggestions (server-side search).
    const [apSearch, setApSearch] = useState("");
    const [apOptions, setApOptions] = useState([]);
    const [apLoading, setApLoading] = useState(false);
    const apRequest = useRef(0);
    const { control, handleSubmit, reset, getValues, formState: { errors } } = useForm({
        defaultValues: { name: "", locations: [emptyLocation()] },
    });
    const { fields, append, remove } = useFieldArray({ control, name: "locations", keyName: "key" });
    useEffect(() => {
        if (!clientId)
            return;
        let active = true;
        setLoadingClient(true);
        clientsApi
            .getById(clientId)
            .then((response) => {
            const client = response.data.data;
            if (!active || !client)
                return;
            reset({
                name: client.name,
                locations: client.locations.length
                    ? client.locations.map((l) => ({ locationId: l.id, name: l.name, inUse: l.inUse }))
                    : [emptyLocation()],
            });
        })
            .catch((error) => {
            toast.error(apiErrorMessage(error, t("common:catalog.clients.loadOneFailed", "Could not load the client.")));
            navigate("/clients");
        })
            .finally(() => active && setLoadingClient(false));
        return () => {
            active = false;
        };
    }, [clientId]);
    useEffect(() => {
        const term = apSearch.trim();
        if (term.length < 1) {
            setApOptions([]);
            return;
        }
        const id = ++apRequest.current;
        setApLoading(true);
        const handle = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                const response = yield accessPointsApi.getAllAccessPoints({
                    pageNumber: 1,
                    pageSize: 20,
                    sortColumn: "accessPointName",
                    sortDirection: "asc",
                    filters: { accessPointName: term },
                });
                if (id !== apRequest.current)
                    return;
                const names = ((_b = (_a = response.data.data) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []).map((ap) => { var _a; return (_a = ap.accessPointName) !== null && _a !== void 0 ? _a : ""; }).filter(Boolean);
                setApOptions(Array.from(new Set(names)));
            }
            catch (_c) {
                if (id === apRequest.current)
                    setApOptions([]);
            }
            finally {
                if (id === apRequest.current)
                    setApLoading(false);
            }
        }), 350);
        return () => clearTimeout(handle);
    }, [apSearch]);
    const onSubmit = (data) => __awaiter(void 0, void 0, void 0, function* () {
        if (saving)
            return;
        setSaving(true);
        try {
            const locations = data.locations
                .filter((l) => l.name.trim() !== "" || l.locationId)
                .map((l) => ({ id: l.locationId || null, name: l.name.trim() }));
            const command = { id: clientId, name: data.name.trim(), locations };
            if (isEdit)
                yield clientsApi.update(command);
            else
                yield clientsApi.create(command);
            toast.success(isEdit
                ? t("common:catalog.clients.updated", "Client updated successfully.")
                : t("common:catalog.clients.created", "Client created successfully."));
            navigate("/clients");
        }
        catch (error) {
            // Values stay in the form; e.g. "These locations are assigned to access points…" or a duplicate name.
            toast.error(apiErrorMessage(error, t("common:catalog.clients.saveFailed", "Could not save the client.")));
        }
        finally {
            setSaving(false);
        }
    });
    const onDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!clientId)
            return;
        setDeleting(true);
        try {
            yield clientsApi.remove(clientId);
            toast.success(t("common:catalog.clients.deleted", "Client deleted."));
            navigate("/clients");
        }
        catch (error) {
            toast.error(apiErrorMessage(error, t("common:catalog.clients.deleteFailed", "Could not delete the client.")));
        }
        finally {
            setDeleting(false);
            setConfirmDelete(false);
        }
    });
    const requestRemove = (index) => {
        const row = getValues(`locations.${index}`);
        if (row.inUse)
            return;
        if (row.locationId)
            setPendingRemoval(index); // a saved location: confirm first
        else
            remove(index);
    };
    const title = isEdit ? t("common:catalog.clients.edit", "Edit Client") : t("common:catalog.clients.add", "Add Client");
    const actionButton = {
        label: isEdit ? t("common:actions.saveChanges", "Save Changes") : title,
        onClick: handleSubmit(onSubmit),
        variant: "contained",
        size: "large",
        disabled: saving || loadingClient,
        loading: saving,
    };
    const uniqueLocation = useMemo(() => (value, index) => {
        const name = value.trim().toLowerCase();
        if (!name)
            return true;
        const duplicate = getValues("locations").some((l, i) => i !== index && l.name.trim().toLowerCase() === name);
        return !duplicate || t("common:catalog.clients.locationDuplicate", "This location is already listed.");
    }, [getValues, t]);
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: title, showBackButton: true, bottomActionButton: actionButton, topActions: isEdit && hasPermission(Permission.CLIENTS_DELETE) ? (React.createElement(SButton, { variant: "soft", color: "error", onClick: () => setConfirmDelete(true), disabled: loadingClient }, t("common:actions.delete", "Delete"))) : undefined }),
        React.createElement(PageContent, { hasBottomAction: true }, loadingClient ? (React.createElement(Box, null,
            React.createElement(Skeleton, { variant: "rounded", height: 56, sx: { mb: 2 } }),
            React.createElement(Skeleton, { variant: "rounded", height: 160 }))) : (React.createElement("form", { onSubmit: handleSubmit(onSubmit) },
            React.createElement(Grid, { container: true, spacing: 2 },
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(Controller, { name: "name", control: control, rules: {
                            validate: (v) => v.trim().length > 0 || t("common:catalog.clients.nameRequired", "Client name is required."),
                            maxLength: { value: 256, message: t("common:catalog.clients.nameTooLong", "Maximum 256 characters.") },
                        }, render: ({ field, fieldState }) => {
                            var _a;
                            return (React.createElement(Autocomplete, { freeSolo: true, options: apOptions, loading: apLoading, filterOptions: (x) => x, inputValue: (_a = field.value) !== null && _a !== void 0 ? _a : "", onInputChange: (_, value, reason) => {
                                    field.onChange(value);
                                    if (reason === "input")
                                        setApSearch(value);
                                }, onChange: (_, value) => field.onChange(typeof value === "string" ? value : ""), noOptionsText: apSearch.trim() ? t("common:catalog.clients.noApMatch", "No access point with this name") : t("common:catalog.clients.typeToSearch", "Type to search access point names"), renderInput: (params) => {
                                    var _a, _b;
                                    return (React.createElement(TextField, Object.assign({}, params, { variant: "filled", label: t("common:catalog.clients.clientName", "Client Name"), error: !!fieldState.error, helperText: (_b = (_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : t("common:catalog.clients.nameHint", "Pick an access point name or type a name."), InputProps: Object.assign(Object.assign({}, params.InputProps), { endAdornment: (React.createElement(React.Fragment, null,
                                                apLoading ? React.createElement(CircularProgress, { color: "inherit", size: 18 }) : null,
                                                params.InputProps.endAdornment)) }) })));
                                } }));
                        } })),
                React.createElement(Grid, { size: { xs: 12 } },
                    React.createElement(Box, { sx: { bgcolor: "background.paper", borderRadius: 4, p: 2 } },
                        React.createElement(Typography, { variant: "h6", sx: { color: "primary.darker", mb: 1.5 } }, t("common:catalog.clients.locations", "Locations")),
                        fields.map((field, index) => {
                            var _a, _b, _c, _d, _e;
                            return (React.createElement(Box, { key: field.key, sx: { display: "flex", alignItems: "flex-start", gap: 1, mb: 1 } },
                                React.createElement(FInput, { name: `locations.${index}.name`, control: control, label: t("common:catalog.clients.location", "Location"), placeholder: t("common:catalog.clients.locationPlaceholder", "e.g. Jordaan District, Amsterdam, NL"), error: (_b = (_a = errors.locations) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b.name, helperText: (_e = (_d = (_c = errors.locations) === null || _c === void 0 ? void 0 : _c[index]) === null || _d === void 0 ? void 0 : _d.name) === null || _e === void 0 ? void 0 : _e.message, rules: {
                                        validate: {
                                            required: (v) => String(v !== null && v !== void 0 ? v : "").trim().length > 0 || (fields.length === 1 && !getValues(`locations.${index}.locationId`))
                                                ? true
                                                : t("common:catalog.clients.locationRequired", "Enter a location or remove the row."),
                                            unique: (v) => uniqueLocation(String(v !== null && v !== void 0 ? v : ""), index),
                                        },
                                        maxLength: { value: 256, message: t("common:catalog.clients.nameTooLong", "Maximum 256 characters.") },
                                    } }),
                                React.createElement(Tooltip, { title: field.inUse
                                        ? t("common:catalog.clients.locationInUse", "Assigned to an access point: remove it from the access point first.")
                                        : t("common:actions.remove", "Remove") },
                                    React.createElement("span", null,
                                        React.createElement(SIconButton, { size: "large", sx: { borderRadius: "50px", mt: 0.5 }, colorVariant: "inherit", "aria-label": t("common:actions.remove", "Remove"), disabled: field.inUse, onClick: () => requestRemove(index) },
                                            React.createElement(Icon, { icon: "material-symbols:close-rounded" }))))));
                        }),
                        React.createElement(Box, { sx: { display: "flex", justifyContent: "flex-end", mt: 1 } },
                            React.createElement(SButton, { variant: "soft", color: "inherit", startIcon: React.createElement(Icon, { icon: "eva:plus-outline", width: 22, height: 22 }), onClick: () => append(emptyLocation()) }, t("common:catalog.clients.addLocation", "Add a Location"))))))))),
        React.createElement(ConfirmDialog, { isOpen: pendingRemoval !== null, onClose: () => setPendingRemoval(null), onConfirm: () => {
                if (pendingRemoval !== null)
                    remove(pendingRemoval);
                setPendingRemoval(null);
            }, actionText: t("common:actions.remove", "Remove"), customMessage: t("common:catalog.clients.confirmRemoveLocation", "Remove this location? It is deleted when you save the client."), variant: "warning" }),
        React.createElement(ConfirmDialog, { isOpen: confirmDelete, onClose: () => setConfirmDelete(false), onConfirm: onDelete, loading: deleting, actionText: t("common:actions.delete", "Delete"), screenName: t("common:catalog.clients.clientLower", "client"), variant: "delete" })));
};
export default AddEditClientPage;
